/**
 * Utilitários para lidar com timestamps do Firestore.
 *
 * O Firestore retorna timestamps de várias formas:
 *   - Objeto Timestamp do SDK admin: { toDate(): Date }
 *   - Objeto serializado em JSON: { _seconds: number, _nanoseconds: number }
 *   - String ISO (quando passa por toISOString)
 *   - Date (quando convertido em algum lugar do pipeline)
 *
 * Estas funções centralizam a conversão para evitar `any` espalhado pelo código
 * e garantir que toda exibição de data use o fuso horário de Brasil.
 */

import { formatDate, formatRelativeTime } from './utils';

export type FirestoreTimestampLike =
  | string
  | number
  | Date
  | { toDate: () => Date }
  | { _seconds: number; _nanoseconds?: number }
  | { seconds: number; nanoseconds?: number }
  | null
  | undefined;

/**
 * Converte qualquer representação de timestamp do Firestore em um Date.
 * Retorna `null` se o valor for inválido ou ausente.
 */
export function parseFirestoreTimestamp(value: FirestoreTimestampLike): Date | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'object') {
    // Firestore Timestamp client SDK
    if ('toDate' in value && typeof value.toDate === 'function') {
      try {
        const date = value.toDate();
        return Number.isNaN(date.getTime()) ? null : date;
      } catch {
        return null;
      }
    }

    // Timestamp serializado via JSON (formato com underscore)
    if ('_seconds' in value && typeof value._seconds === 'number') {
      return new Date(value._seconds * 1000);
    }

    // Timestamp serializado via JSON (formato sem underscore)
    if ('seconds' in value && typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
  }

  return null;
}

/**
 * Formata um timestamp do Firestore como data BR (DD/MM/YYYY).
 * Retorna string vazia se o valor for inválido.
 */
export function formatFirestoreDate(
  value: FirestoreTimestampLike,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = parseFirestoreTimestamp(value);
  if (!date) return '';
  return formatDate(date, options);
}

/**
 * Formata um timestamp do Firestore como tempo relativo ("agora", "5 min atrás", etc.).
 * Retorna string vazia se o valor for inválido.
 */
export function formatFirestoreRelative(value: FirestoreTimestampLike): string {
  const date = parseFirestoreTimestamp(value);
  if (!date) return '';
  return formatRelativeTime(date);
}
